import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Branch, CreateStaffMember, StaffMember } from "@dentacore/shared-types";

import styles from "./settings.module.css";
import { StaffForm } from "./staff-form";
import { Pager, fitArea, useRowPages } from "../ui/paging";

/**
 * Who the filters are showing, and it is **everyone by default**.
 *
 * The redesign proposal drew this defaulting to active people only, and that is
 * the wrong default here: somebody who has not accepted their invite yet is
 * `isActive: false` (see `StaffMember`), so an admin who had just added a
 * colleague would open this screen and not find them. "Where did they go" is a
 * support call; a list with a filter on it is not.
 */
type Status = "all" | "active" | "no-access";

/**
 * How many people a clinic needs before the filters are worth their space.
 *
 * **Not a clinic-configured value** — it is a property of the screen, like the
 * page's own column width, and no admin has an opinion about it. Eight is about
 * where the list stops fitting beside the card head on a laptop.
 */
const FILTERS_FROM = 8;

/**
 * The clinic's people — **level one of the same drill-down as locations** (A1).
 *
 * **Not the `/staff` destination.** That one draws the rota — patterns, day
 * exceptions, time off, leave — which every role reads and Phase 9 builds. This
 * is accounts, roles and access: admin-only work, so it lives behind the
 * admin-only guard the rest of Settings already sits behind (S3).
 *
 * **Adding someone hands back a link, never a password** (STORY-064). The link
 * is shown once, here, because the server keeps only a hash of it.
 */
export function PeopleSection({
  staff,
  branches,
  busy,
  invite,
  onCreate,
  onDismissInvite,
}: {
  staff: readonly StaffMember[] | null;
  /**
   * The clinic's locations — for the line under each row, and for the location
   * filter. **The list rather than a count**, because the filter has to name
   * them; an empty list is a clinic mid-setup and the filter hides itself.
   */
  branches: readonly Branch[];
  busy: boolean;
  /**
   * The link just issued, if any — held by the page, shown by this section.
   *
   * **A whole URL from the server, not a token to assemble** (QA20-5). This used
   * to be a token with a `linkFor` helper built on `window.location.origin`, and
   * D3 lets an admin run their clinic from the apex — where that helper produced
   * a link the accept page cannot resolve, and the invitee was then dropped into
   * the inviter's dashboard.
   */
  /** `link` is null for someone without `access.assign`: the invite went by email only (QF-D24). */
  invite: { name: string; link: string | null; emailed: boolean } | null;
  /**
   * Resolves `true` when the person was actually created. **The panel closes on
   * that and on nothing else** — closing it optimistically is what used to
   * throw away everything typed when the server refused.
   */
  onCreate: (body: CreateStaffMember) => Promise<boolean>;
  onDismissInvite: () => void;
}): React.JSX.Element {
  const [adding, setAdding] = useState(false);

  /**
   * The last refusal, kept so the form can point at the field it is about
   * rather than only printing a sentence above it.
   */
  const [refusal, setRefusal] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [role, setRole] = useState("");
  const [branchId, setBranchId] = useState("");
  const [status, setStatus] = useState<Status>("all");

  /**
   * The roles actually present, rather than the enum.
   *
   * `role` is an enum in the schema, but offering every value would list
   * filters that match nothing in most clinics — and a practice with no
   * hygienist does not want one in a dropdown. Derived from what loaded, so it
   * maintains itself, and sorted by the label a person reads rather than by the
   * underlying constant.
   */
  const roles = useMemo(() => {
    const present = new Set((staff ?? []).map((person) => person.role));
    return [...present].sort((a, b) => roleLabel(a).localeCompare(roleLabel(b)));
  }, [staff]);

  /**
   * **All four filters narrow together, and the search matches email too.**
   *
   * Email rather than name alone because that is what an admin has in front of
   * them when somebody reports they cannot sign in. Everything here is already
   * in the `GET /users` payload, so this asks the server for nothing — at the
   * sizes a single practice reaches, a filter that costs a round trip is slower
   * than one that does not.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return (staff ?? []).filter((person) => {
      if (status === "active" && !person.isActive) return false;
      if (status === "no-access" && person.isActive) return false;
      if (role !== "" && person.role !== role) return false;
      if (branchId !== "" && !person.memberships.some((m) => m.branchId === branchId)) return false;

      if (needle === "") return true;
      const haystack = `${person.firstName} ${person.lastName} ${person.email}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [staff, query, role, branchId, status]);

  const filtering = query.trim() !== "" || role !== "" || branchId !== "" || status !== "all";

  // A busy practice has more people than a screen holds: whole rows, paged.
  const paging = useRowPages({ count: shown.length, estimate: 66, resetOn: shown });

  const clear = (): void => {
    setQuery("");
    setRole("");
    setBranchId("");
    setStatus("all");
  };

  return (
    <section className={`${styles.card} ${styles.fitCard}`}>
      <div className={styles.cardHead}>
        {/*
          **The count says what the filters are doing to it.** A bare "31
          people" above four rows is the screen lying about what it is showing,
          and it is the one number somebody checks before concluding a colleague
          is missing.
        */}
        <div>
          <h3 className={styles.cardTitle} data-testid="staff-count">
            {staff === null
              ? "Everyone who can sign in"
              : filtering
                ? `${String(shown.length)} of ${String(staff.length)}`
                : `${String(staff.length)} ${staff.length === 1 ? "person" : "people"}`}
          </h3>
        </div>
        {!adding && staff !== null && (
          <button
            type="button"
            className={styles.primary}
            disabled={busy}
            onClick={() => {
              setAdding(true);
              setRefusal(null);
            }}
            data-testid="staff-add-open"
          >
            Add someone
          </button>
        )}
      </div>

      {adding && (
        <div className={styles.cardBody}>
          <h4 className={styles.panelTitle}>Someone new</h4>
          <StaffForm
            busy={busy}
            refusal={refusal}
            onCancel={() => {
              // Leaving is the one thing that discards what was typed — the
              // form unmounts, so its state goes with it.
              setAdding(false);
              setRefusal(null);
            }}
            onSubmit={(body) => {
              void onCreate(body).then((created) => {
                if (created) {
                  setAdding(false);
                  setRefusal(null);
                } else {
                  // Everything the person typed is still in the form. This only
                  // tells it which field to mark.
                  setRefusal(body.email);
                }
              });
            }}
          />
        </div>
      )}

      {/*
        **Shown once and only once.** The server stores a hash, so this is the
        single moment the link exists anywhere it can be read — which is why it
        does not vanish on a timer and has to be dismissed deliberately.
      */}
      {invite !== null && (
        <div
          className={`${styles.cardBody} ${styles.cardBodyPanel}`}
          data-testid="invite-link-panel"
        >
          <h4 className={styles.panelTitle}>
            {invite.emailed ? `Invite sent to ${invite.name}` : `${invite.name}'s invite link`}
          </h4>
          {/*
            **Which of the two happened, said plainly** (QA20-4). The invite is
            emailed now, and the link stays here — so the only question left is
            whether the admin has to do anything, and that is exactly what this
            sentence answers. Saying "sent" when it was not is how somebody
            waits a day for an email that never left.
          */}
          {invite.link === null ? (
            /*
              **No link to show** (QF-D24): only someone who can change access
              sees an invite link, since whoever holds it can choose the
              password. So the sentence is all there is.
            */
            <p className={styles.blockNote} data-testid="invite-sent-state">
              {invite.emailed ? (
                <>We have emailed them a link to set their password. It works for seven days.</>
              ) : (
                <>
                  <strong>The email did not go out.</strong> Ask an administrator to send a new
                  invite from their page &mdash; only someone who can change access sees the link.
                </>
              )}
            </p>
          ) : (
            <p className={styles.blockNote} data-testid="invite-sent-state">
              {invite.emailed ? (
                <>
                  We have emailed them a link to set their password.{" "}
                  <strong>This copy is a backup</strong> &mdash; hand it over if the email does not
                  arrive.
                </>
              ) : (
                <>
                  <strong>The email did not go out.</strong> The invite is valid &mdash; send this
                  link to them yourself.
                </>
              )}{" "}
              <strong>It is shown once</strong> &mdash; the server keeps only a hash and cannot show
              it again. It works for seven days, and issuing a new one replaces it.
            </p>
          )}
          {invite.link !== null && (
            <input
              className={styles.input}
              readOnly
              value={invite.link}
              onFocus={(event) => event.currentTarget.select()}
              aria-label="Invite link"
              data-testid="invite-link"
            />
          )}
          <div className={styles.formActions}>
            {invite.link !== null && (
              <button
                type="button"
                className={styles.primary}
                onClick={() => void navigator.clipboard?.writeText(invite.link ?? "")}
                data-testid="invite-copy"
              >
                Copy link
              </button>
            )}
            <button
              type="button"
              className={styles.ghost}
              onClick={onDismissInvite}
              data-testid="invite-dismiss"
            >
              {invite.link === null ? "Done" : "I have sent it"}
            </button>
          </div>
        </div>
      )}

      {/*
        **The filters appear once there is enough to filter.** Four controls
        over six rows is more chrome than list; they earn their space when the
        list stops fitting on a screen, which is the case this exists for — the
        seeded clinic alone renders 31.
      */}
      {staff !== null && staff.length > FILTERS_FROM && (
        <div className={styles.cardBody}>
          <div className={styles.filters}>
            <input
              type="search"
              className={`${styles.input} ${styles.filterGrow}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name or email&hellip;"
              aria-label="Search people"
              data-testid="staff-search"
            />

            <select
              className={styles.input}
              value={role}
              onChange={(event) => setRole(event.target.value)}
              aria-label="Filter by role"
              data-testid="staff-filter-role"
            >
              <option value="">All roles</option>
              {roles.map((name) => (
                <option key={name} value={name}>
                  {roleLabel(name)}
                </option>
              ))}
            </select>

            {branches.length > 0 && (
              <select
                className={styles.input}
                value={branchId}
                onChange={(event) => setBranchId(event.target.value)}
                aria-label="Filter by location"
                data-testid="staff-filter-location"
              >
                <option value="">All locations</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            )}

            <select
              className={styles.input}
              value={status}
              onChange={(event) => setStatus(event.target.value as Status)}
              aria-label="Filter by access"
              data-testid="staff-filter-status"
            >
              <option value="all">Everyone</option>
              <option value="active">Can sign in</option>
              <option value="no-access">No access</option>
            </select>

            {filtering && (
              <button
                type="button"
                className={styles.ghost}
                onClick={clear}
                data-testid="staff-filter-clear"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {staff === null ? (
        <div className={styles.cardBody}>
          <p className={styles.quiet}>Reading&hellip;</p>
        </div>
      ) : staff.length === 0 ? (
        <div className={styles.cardBody}>
          <p className={styles.blank} data-testid="no-staff">
            Nobody here yet.
          </p>
        </div>
      ) : shown.length === 0 ? (
        /*
          **Says what would widen it, rather than only that nothing matched.**
          Four filters narrow together, so "no results" on its own leaves
          somebody guessing which one to undo.
        */
        <div className={styles.cardBody}>
          <p className={styles.blank} data-testid="no-staff-match">
            Nobody here matches those filters.{" "}
            <button
              type="button"
              className={styles.linkish}
              onClick={clear}
              data-testid="staff-filter-clear-blank"
            >
              Clear them
            </button>{" "}
            to see all {staff.length} again.
          </p>
        </div>
      ) : (
        <>
          <div className={fitArea} ref={paging.areaRef}>
            <ul className={styles.cardRows}>
              {shown.slice(paging.start, paging.end).map((person, index) => (
                <li key={person.id} ref={index === 0 ? paging.rowRef : undefined}>
                  <Link
                    to={`/people/staff/${person.id}`}
                    className={styles.row}
                    data-testid="staff-row"
                  >
                    <span className={styles.rowBody}>
                      <span className={styles.rowTitle}>
                        <span className={styles.rowName} data-testid="staff-name">
                          {person.firstName} {person.lastName}
                        </span>
                        {/*
                      **The role as a pill, and no longer only in the meta
                      line.** It is what the list is most often scanned for —
                      "who are the hygienists" — and 11px grey prose is the
                      wrong weight for the answer. The meta keeps the locations,
                      which is the other half of the same question.
                    */}
                        <span className={styles.tagRole} data-testid="staff-role">
                          {roleLabel(person.role)}
                        </span>
                        {!person.isActive && (
                          <span className={styles.tagQuiet} data-testid="staff-inactive">
                            No access
                          </span>
                        )}
                      </span>
                      <span className={styles.rowMeta}>
                        {membershipSummary(person, branches.length)}
                      </span>
                    </span>
                    <span className={styles.chevron} aria-hidden="true">
                      ›
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.fitFoot}>
            <Pager
              page={paging.page}
              count={paging.pages}
              onPage={paging.setPage}
              from={paging.start + 1}
              to={paging.end}
              total={shown.length}
              noun="people"
              testId="staff-pager"
            />
          </div>
        </>
      )}
    </section>
  );
}

/**
 * `FRONT_DESK` → `Front desk`.
 *
 * **Derived, not a lookup table.** The roles are a structural enum the program
 * branches on, so a hand-written map would be a second place to update every
 * time one is added — and the enum is the kind of thing that gains a member.
 */
export function roleLabel(role: string): string {
  const words = role.toLowerCase().split("_");
  return words
    .map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/** "2 of 4 locations", or something truer when the numbers are small. */
function membershipSummary(person: StaffMember, branchCount: number): string {
  const mine = person.memberships.length;
  if (mine === 0) return "no locations yet";
  if (branchCount > 0 && mine === branchCount) return "every location";
  return `${mine} of ${branchCount} locations`;
}
